import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Container } from '@/components/ui/container';
import { Inline } from '@/components/ui/layout';
import { Text } from '@/components/ui/typography';
import { cn } from '@/lib/utils';
import { ChevronRight } from 'lucide-react';

const breadcrumbMap = {
    'dashboard': ['Dashboard'],
    'add-company': ['Add Company Profile'],
    'all-companies': ['All Companies'],
    'company-profile': ['All Companies', 'COMPANY_NAME', 'Company Profile'],
    'edit-company-profile': ['All Companies', 'COMPANY_NAME', 'Edit Company Profile'],
    // Note: Delete Company Profile is a popup, not a direct navigation target
    'view-jobs': ['All Companies', 'COMPANY_NAME', 'View Posted Jobs'],
    'create-jd': ['All Companies', 'COMPANY_NAME', 'Post Job', 'Create JD'],
    'upload-jd': ['All Companies', 'COMPANY_NAME', 'Post Job', 'Upload JD'],
    'generate-jd': ['All Companies', 'COMPANY_NAME', 'Post Job', 'Generate JD with AI'],
    'after-jd': ['All Companies', 'COMPANY_NAME', 'Post Job'], // This is the main Post Job breadcrumb, shown when dropdown is open

    // Job-specific actions
    'job-description': ['All Companies', 'COMPANY_NAME', 'View Posted Jobs', 'JOB_NAME', 'Job Description'],
    'enroll-candidate': ['All Companies', 'COMPANY_NAME', 'View Posted Jobs', 'JOB_NAME', 'Enroll Candidate'], // This is a popup, but breadcrumb shows context
    'view-scheduled-tests': ['All Companies', 'COMPANY_NAME', 'View Posted Jobs', 'JOB_NAME', 'View Scheduled Tests'],
    'view-all-candidates': ['All Companies', 'COMPANY_NAME', 'View Posted Jobs', 'JOB_NAME', 'View All Candidates'],
    'edit-job': ['All Companies', 'COMPANY_NAME', 'View Posted Jobs', 'JOB_NAME', 'Edit Job'],
    // Note: Delete Job is a popup, not a direct navigation target
};

// Helper to get base path for breadcrumbs
const getBasePath = (crumb) => {
    const baseUrl = '/company-admin-home';
    switch (crumb) {
        // case 'Dashboard': return `${baseUrl}/dashboard`;
        // case 'All Companies': return `${baseUrl}/companies`;
        // case 'Add Company Profile': return `${baseUrl}/companies/add`;
        // case 'Company Profile': return `${baseUrl}/companies/profile`; // Simplified, would need actual ID
        // case 'View Posted Jobs': return `${baseUrl}/companies/jobs`; // Simplified, would need actual ID
        // case 'Post Job': return `${baseUrl}/companies/jobs/create`; // Default to create JD
        // Add more cases for other breadcrumb parts if they should be clickable
        default: return null; // Not clickable
    }
};


export default function Breadcrumbs({ activeMain, selectedCompany, selectedJob, setSelectedCompany, setSelectedJob, navigate }) {
    const location = useLocation();
    let crumbs = breadcrumbMap[activeMain] || [];
    const jobID = new URLSearchParams(location.search).get("jobID");
    const companyID = new URLSearchParams(location.search).get("companyID");
    const [jobName, setJobName] = useState('');
    const [companyName, setCompanyName] = useState('');

    useEffect(() => {
        const fetchJobDescription = async (jobId) => {
            try {
                const response = await fetch(`/api/job_description?jobId=${jobId}`);
                if (response.ok) {
                    const jobData = await response.json();
                    console.log("Job Description:", jobData);
                    setJobName(jobData.job_title);
                }
            } catch (error) {
                console.error("Error fetching job description:", error);
            }
        };

        if (jobID) {
            fetchJobDescription(jobID);
        }
    }, [jobID]); // Only jobID needs to be here

    // If you still want to see jobName after it's updated:
    useEffect(() => {
        console.log("Updated jobName:", jobName);
    }, [jobName]);


    const fetchCompanyByID = async (companyID) => {
        try {
            console.log("companyID", companyID);
            const response = await fetch(`/api/get_company_by_ID?companyID=${encodeURIComponent(companyID)}`, {
                method: "GET",
                // headers: { "Content-Type": "application/json" },
            });
            if (!response.ok) {
                throw new Error("Failed to fetch company");
            }
            const data = await response.json();
            console.log("Company Data:", data);
            setCompanyName(data.company_name); // Set the company data to state
            // setCompanyEmail(data.email); // Set the company email from the fetched data
            return data;
        } catch (error) {
            console.error("Error fetching company:", error);
            return null;
        }
    };

    useEffect(() => {
        const fetchData = async () => {
            await fetchCompanyByID(companyID);
        };
        if (companyID) fetchData();
        console.log(companyID, companyName)
    }, [companyID]);

    // Replace 'COMPANY_NAME' placeholder with the actual selected company name
    if (companyName && crumbs.includes('COMPANY_NAME')) {
        crumbs = crumbs.map(crumb => crumb === 'COMPANY_NAME' ? companyName : crumb);
    }

    // Replace 'JOB_NAME' placeholder with the actual selected job title
    if (jobName && crumbs.includes('JOB_NAME')) {
        crumbs = crumbs.map(crumb => crumb === 'JOB_NAME' ? jobName : crumb);
    }

    return (
        <Container>
            <div className={cn("p-3 text-base text-muted-foreground shadow-sm")}>
                <Inline spacing="sm" align="center">
                    {crumbs.map((crumb, index) => {
                        const path = getBasePath(crumb);
                        const isLast = index === crumbs.length - 1;

                        return (
                            <React.Fragment key={index}>
                                {path && !isLast ? (
                                    <a
                                        href="#"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            navigate(path);
                                        }}
                                        className="text-muted-foreground hover:text-foreground transition-colors"
                                    >
                                        {crumb}
                                    </a>
                                ) : (
                                    <Text className={cn("text-muted-foreground", isLast && "font-semibold text-primary")}>
                                        {crumb}
                                    </Text>
                                )}
                                {index < crumbs.length - 1 && (
                                    <ChevronRight className="w-4 h-4 text-muted-foreground" />
                                )}
                            </React.Fragment>
                        );
                    })}
                </Inline>
            </div>
        </Container>
    );
}